// /src/components/tools/memberProfileCard/PerkThemeHaloFrostCanvas.jsx
// Hoarfrost / fern-crystal givre growing inward from edges (frozen 0–100).
// Organic dendrites with irregular feather — no clipped rim band / stencil.

import React, { useEffect, useRef } from 'react';
import {
  clampPerkThemeHaloFrozen,
  sanitizePerkThemeHaloColor,
} from '../../../utils/perkThemeHalo';

function clamp01(n) {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

function createRng(seed) {
  let t = seed >>> 0;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function iceTint(rgb, towardWhite = 0.7) {
  return {
    r: Math.round(rgb.r + (245 - rgb.r) * towardWhite),
    g: Math.round(rgb.g + (250 - rgb.g) * towardWhite),
    b: Math.round(rgb.b + (255 - rgb.b) * towardWhite * 0.9),
  };
}

/** Point + inward normal on rectangle perimeter. */
function perimeterSeed(u, w, h, rng) {
  const perim = 2 * (w + h);
  let d = (((u % 1) + 1) % 1) * perim;
  // Slight jitter so seeds aren't evenly spaced
  d = (d + (rng() - 0.5) * perim * 0.01 + perim) % perim;
  if (d < w) return { x: d, y: 0, nx: 0, ny: 1 };
  d -= w;
  if (d < h) return { x: w, y: d, nx: -1, ny: 0 };
  d -= h;
  if (d < w) return { x: w - d, y: h, nx: 0, ny: -1 };
  d -= w;
  return { x: 0, y: h - d, nx: 1, ny: 0 };
}

/**
 * Draw a fern-like hoarfrost crystal (spine + fine needles + recursive forks).
 */
function drawFernCrystal(ctx, ox, oy, angle, length, depth, rng, ice, alphaScale) {
  if (depth <= 0 || length < 1.2) return;

  const tipX = ox + Math.cos(angle) * length;
  const tipY = oy + Math.sin(angle) * length;

  // Main spine — brighter, slightly thicker at root
  const rootW = Math.max(0.45, length * 0.035);
  const tipW = Math.max(0.25, rootW * 0.35);
  const grad = ctx.createLinearGradient(ox, oy, tipX, tipY);
  // Fade hard toward tip so crystals dissolve instead of ending as a rim line
  grad.addColorStop(0, `rgba(255,255,255,${(0.58 * alphaScale).toFixed(3)})`);
  grad.addColorStop(0.45, `rgba(${ice.r},${ice.g},${ice.b},${(0.32 * alphaScale).toFixed(3)})`);
  grad.addColorStop(0.82, `rgba(255,255,255,${(0.08 * alphaScale).toFixed(3)})`);
  grad.addColorStop(1, `rgba(255,255,255,0)`);

  ctx.lineCap = 'round';
  ctx.strokeStyle = grad;
  ctx.lineWidth = rootW;
  ctx.beginPath();
  ctx.moveTo(ox, oy);
  // Soft curve for organic spine
  const midBend = (rng() - 0.5) * length * 0.18;
  const px = -Math.sin(angle);
  const py = Math.cos(angle);
  const mx = ox + Math.cos(angle) * length * 0.5 + px * midBend;
  const my = oy + Math.sin(angle) * length * 0.5 + py * midBend;
  ctx.quadraticCurveTo(mx, my, tipX, tipY);
  ctx.stroke();

  // Fine feather needles along the spine (fern look)
  const needleCount = Math.max(4, Math.round(length / (1.6 + rng())));
  for (let i = 1; i <= needleCount; i += 1) {
    const t = i / (needleCount + 0.5);
    // Sample point along the quadratic
    const u = 1 - t;
    const sx = u * u * ox + 2 * u * t * mx + t * t * tipX;
    const sy = u * u * oy + 2 * u * t * my + t * t * tipY;

    const side = i % 2 === 0 ? 1 : -1;
    const nLen = length * (0.12 + (1 - t) * 0.38) * (0.45 + rng() * 0.7);
    const nAngle = angle + side * (0.55 + rng() * 0.75);
    const nx = sx + Math.cos(nAngle) * nLen;
    const ny = sy + Math.sin(nAngle) * nLen;

    const na = (0.18 + (1 - t) * 0.35) * alphaScale * (0.55 + rng() * 0.45);
    ctx.strokeStyle = `rgba(255,255,255,${na.toFixed(3)})`;
    ctx.lineWidth = Math.max(0.2, tipW * (1.1 - t));
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.lineTo(nx, ny);
    ctx.stroke();

    // Micro-barbs on longer needles
    if (nLen > 6 && rng() < 0.55) {
      const barbT = 0.45 + rng() * 0.4;
      const bx = sx + (nx - sx) * barbT;
      const by = sy + (ny - sy) * barbT;
      const bLen = nLen * (0.25 + rng() * 0.35);
      const bAngle = nAngle + side * (0.4 + rng() * 0.5);
      ctx.strokeStyle = `rgba(255,255,255,${(na * 0.65).toFixed(3)})`;
      ctx.lineWidth = 0.25;
      ctx.beginPath();
      ctx.moveTo(bx, by);
      ctx.lineTo(bx + Math.cos(bAngle) * bLen, by + Math.sin(bAngle) * bLen);
      ctx.stroke();
    }
  }

  // Recursive tip fork (asymmetric — irregular growth)
  const forkChance = depth > 2 ? 0.92 : 0.55;
  if (rng() < forkChance) {
    const left = angle - (0.25 + rng() * 0.45);
    const right = angle + (0.2 + rng() * 0.5);
    const shrink = 0.52 + rng() * 0.22;
    drawFernCrystal(ctx, tipX, tipY, left, length * shrink, depth - 1, rng, ice, alphaScale * 0.9);
    if (rng() < 0.7) {
      drawFernCrystal(ctx, tipX, tipY, right, length * shrink * (0.7 + rng() * 0.35), depth - 1, rng, ice, alphaScale * 0.85);
    }
  }

  // Occasional mid-spine major side branch
  if (depth > 1 && rng() < 0.4) {
    const t = 0.3 + rng() * 0.45;
    const u = 1 - t;
    const sx = u * u * ox + 2 * u * t * mx + t * t * tipX;
    const sy = u * u * oy + 2 * u * t * my + t * t * tipY;
    const side = rng() > 0.5 ? 1 : -1;
    drawFernCrystal(
      ctx,
      sx,
      sy,
      angle + side * (0.7 + rng() * 0.5),
      length * (0.35 + rng() * 0.3),
      depth - 1,
      rng,
      ice,
      alphaScale * 0.8,
    );
  }
}

function paintSoftRimGlow(ctx, w, h, rimPx, ice, intensity, rng) {
  // Soft edge glow only — no clipped rectangle band
  const perim = 2 * (w + h);
  const patches = Math.ceil(perim / 5);
  for (let i = 0; i < patches; i += 1) {
    const seed = perimeterSeed((i + rng() * 0.35) / patches, w, h, rng);
    const depth = rng() * rimPx * 0.55;
    const cx = seed.x + seed.nx * depth;
    const cy = seed.y + seed.ny * depth;
    // Irregular radii so the glow front isn't a straight inset
    const r = 5 + rng() * (8 + intensity * 10) * (0.6 + rng() * 0.7);
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    const a = (0.1 + intensity * 0.22) * (0.5 + rng() * 0.55);
    g.addColorStop(0, `rgba(255,255,255,${Math.min(0.42, a * 1.2).toFixed(3)})`);
    g.addColorStop(0.4, `rgba(${ice.r},${ice.g},${ice.b},${(a * 0.4).toFixed(3)})`);
    g.addColorStop(1, 'rgba(200,230,255,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
  }
}

export default function PerkThemeHaloFrostCanvas({ color, frozen = 0 }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);

  const safeColor = sanitizePerkThemeHaloColor(color);
  const safeFrozen = clampPerkThemeHaloFrozen(frozen);

  useEffect(() => {
    if (!safeColor || safeFrozen <= 0) return undefined;

    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const rgb = hexToRgb(safeColor);
    const ice = iceTint(rgb, 0.72);
    const intensity = clamp01(safeFrozen / 100);
    const seed = (parseInt(safeColor.replace('#', ''), 16) ^ (safeFrozen * 7919)) >>> 0;

    const paint = () => {
      const w = Math.max(1, wrap.clientWidth || 1);
      const h = Math.max(1, wrap.clientHeight || 1);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const cw = Math.max(1, Math.round(w * dpr));
      const ch = Math.max(1, Math.round(h * dpr));
      if (canvas.width !== cw || canvas.height !== ch) {
        canvas.width = cw;
        canvas.height = ch;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      const maxDist = Math.min(w, h) * 0.5;
      // How far frost can reach toward center (organic max, not a hard cutoff)
      const coverage = Math.max(12, maxDist * (0.12 + intensity * 0.92));
      // Typical near-edge density zone — lengths vary widely around this, no stencil
      const nearEdge = Math.min(14, Math.max(6, coverage * 0.35));
      const rng = createRng(seed);
      const perim = 2 * (w + h);

      // 1) Soft scattered glow at edges (no rectangular clip band)
      paintSoftRimGlow(ctx, w, h, nearEdge, ice, intensity, rng);

      // 2) Dense short crystals near the edge — heavily varied lengths = jagged front
      const rimSeeds = Math.ceil(perim / 3.8);
      for (let i = 0; i < rimSeeds; i += 1) {
        const u = i / rimSeeds;
        const seedPt = perimeterSeed(u, w, h, rng);
        const baseAngle = Math.atan2(seedPt.ny, seedPt.nx) + (rng() - 0.5) * 0.85;
        // Power bias: many short, some medium — never a uniform inset depth
        const roll = rng() ** 1.35;
        const length = nearEdge * (0.25 + roll * 1.35) * (0.7 + rng() * 0.55);
        const fade = 0.85 - (length / (nearEdge * 2.2)) * 0.45;
        drawFernCrystal(
          ctx,
          seedPt.x,
          seedPt.y,
          baseAngle,
          length,
          2 + (rng() < 0.4 ? 1 : 0),
          rng,
          ice,
          Math.max(0.35, fade) * (0.65 + intensity * 0.4),
        );
      }

      // Extra lace: short/medium mix so perimeter stays dense without a hard rim line
      const rimExtra = Math.ceil(perim / (5.5 - intensity * 3));
      for (let i = 0; i < rimExtra; i += 1) {
        const u = (i + 0.5) / rimExtra + (rng() - 0.5) * 0.012;
        const seedPt = perimeterSeed(u, w, h, rng);
        const baseAngle = Math.atan2(seedPt.ny, seedPt.nx) + (rng() - 0.5) * 1.2;
        const length = nearEdge * (0.15 + rng() ** 1.6 * 1.1);
        drawFernCrystal(
          ctx,
          seedPt.x,
          seedPt.y,
          baseAngle,
          length,
          2,
          rng,
          ice,
          0.45 + intensity * 0.4 + rng() * 0.15,
        );
      }

      // Fine hairlines of irregular length (feather, not a band)
      const hairs = Math.ceil(perim / 2.8);
      ctx.lineCap = 'round';
      for (let i = 0; i < hairs; i += 1) {
        const seedPt = perimeterSeed((i + rng() * 0.4) / hairs, w, h, rng);
        const ang = Math.atan2(seedPt.ny, seedPt.nx) + (rng() - 0.5) * 1.5;
        const len = 2 + rng() ** 1.4 * (nearEdge * 1.4 + coverage * 0.15);
        const a = (0.12 + intensity * 0.28) * (0.4 + rng() * 0.6) * (1 - len / (coverage + 8));
        if (a < 0.04) continue;
        ctx.strokeStyle = `rgba(255,255,255,${a.toFixed(3)})`;
        ctx.lineWidth = 0.3 + rng() * 0.7;
        ctx.beginPath();
        ctx.moveTo(seedPt.x, seedPt.y);
        ctx.lineTo(
          seedPt.x + Math.cos(ang) * len,
          seedPt.y + Math.sin(ang) * len,
        );
        ctx.stroke();
      }

      // 3) Longer dendrites that poke irregularly toward the center
      const clusters = Math.round(8 + intensity * 34);
      for (let i = 0; i < clusters; i += 1) {
        const u = (i + rng() * 0.85) / clusters + (rng() - 0.5) * 0.02;
        const seedPt = perimeterSeed(u, w, h, rng);
        const baseAngle = Math.atan2(seedPt.ny, seedPt.nx) + (rng() - 0.5) * 0.55;
        // Strong length variance — some barely leave the edge, some reach far
        const reachRoll = rng() ** (1.1 + (1 - intensity) * 0.5);
        const length = nearEdge * 0.4 + coverage * (0.15 + reachRoll * 0.9);
        const depth = length > coverage * 0.45
          ? 4 + (rng() < 0.45 ? 1 : 0)
          : 2 + (rng() < 0.5 ? 1 : 0);
        // Fade more as crystals reach inward (no hard opacity cliff)
        const reachFrac = Math.min(1, length / coverage);
        const alphaScale = (0.55 + intensity * 0.4) * (1 - reachFrac * 0.55) * (0.75 + rng() * 0.3);

        drawFernCrystal(
          ctx,
          seedPt.x,
          seedPt.y,
          baseAngle,
          length,
          depth,
          rng,
          ice,
          alphaScale,
        );

        if (rng() < 0.35 + intensity * 0.3) {
          const u2 = u + (rng() - 0.5) * 0.028;
          const s2 = perimeterSeed(u2, w, h, rng);
          drawFernCrystal(
            ctx,
            s2.x,
            s2.y,
            Math.atan2(s2.ny, s2.nx) + (rng() - 0.5) * 0.9,
            length * (0.25 + rng() * 0.45),
            Math.max(2, depth - 1),
            rng,
            ice,
            alphaScale * 0.7,
          );
        }
      }

      // Sparse deeper flecks — soft, low opacity
      const strays = Math.round(intensity * 12);
      for (let i = 0; i < strays; i += 1) {
        const s = perimeterSeed(rng(), w, h, rng);
        const dist = nearEdge + (coverage - nearEdge) * (0.35 + rng() * 0.65);
        drawFernCrystal(
          ctx,
          s.x + s.nx * dist,
          s.y + s.ny * dist,
          Math.atan2(s.ny, s.nx) + (rng() - 0.5) * 1.3,
          coverage * (0.06 + rng() * 0.14),
          2,
          rng,
          ice,
          0.18 + rng() * 0.28,
        );
      }
    };

    paint();
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => paint())
      : null;
    if (ro) ro.observe(wrap);
    window.addEventListener('resize', paint);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', paint);
    };
  }, [safeColor, safeFrozen]);

  if (!safeColor || safeFrozen <= 0) return null;

  return (
    <div
      ref={wrapRef}
      className="perk-theme-frame-halo perk-theme-frame-halo--frost"
      aria-hidden
    >
      <canvas ref={canvasRef} className="perk-theme-frame-halo__canvas" />
    </div>
  );
}
